/**
 * Shared layout for the StreamView / OBS overlay (issue #9) — used by both the
 * public, unauthenticated `/overlay/:token` page ("render" mode) and the authed
 * StreamView tab's live editor ("edit" mode), so corner placement, margins, the
 * font, and the video embed only exist in one place.
 *
 * Corner placement uses Pointer Events, not native HTML5 drag-and-drop — the
 * target is computed geometrically (which quadrant the pointer released over)
 * rather than relying on the browser's dragover/drop hit-testing against tiny
 * target elements, which turned out to be fragile when a dropzone sits
 * underneath the very box occupying that corner. Video size is a slider in
 * StreamView.tsx (`overlayVideoWidth`), not drag-to-resize — simpler, and
 * matches the opacity/font-size controls.
 *
 * Deliberately does NOT use the app's `--surface`/`--bg` dark-panel palette —
 * this gets composited over arbitrary game/webcam footage, not the app chrome,
 * so it carries its own minimal, always-legible styling (see the "Overlay
 * canvas" block in index.css).
 */
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent,
} from "react";
import type { OverlayCorner } from "../lib/api";
import { ensureOverlayFontLoaded, overlayFontFamily } from "../lib/overlayFonts";
import { displayTitle, videoId } from "../lib/youtube";
import { useVideoTitle } from "../hooks/useVideoTitle";
import { loadYouTubeIframeApi, type YTPlayer } from "../lib/youtubeIframeApi";

const CORNERS: OverlayCorner[] = ["top-left", "top-right", "bottom-left", "bottom-right"];

// The now-playing/video boxes are sized in fixed px, so on a narrow container
// (StreamView's editor preview on mobile, or this page loaded directly on a
// phone) they end up disproportionately huge relative to the shrunken canvas.
// `fitCanvas` lays the canvas out at this fixed "design" width and scales the
// whole thing down to fit the actual container, keeping every element's
// proportion to the canvas constant regardless of how small that box gets.
// Below this width the scale is capped at 1 — a container/viewport at or
// above it (any real OBS canvas resolution: 1280, 1920, ...) renders exactly
// as before, literal px, no scaling.
const CANVAS_DESIGN_WIDTH = 640;

/** Design-space size + scale factor to make a CANVAS_DESIGN_WIDTH-wide canvas
 * exactly fill a `containerWidth` × `containerHeight` box, whatever its aspect
 * ratio (the editor preview is locked to 16:9, but the real overlay page fills
 * an arbitrary viewport — a phone screen isn't 16:9). */
function fitCanvas(containerWidth: number, containerHeight: number) {
  const scale = Math.min(1, containerWidth / CANVAS_DESIGN_WIDTH);
  return { scale, width: containerWidth / scale, height: containerHeight / scale };
}

// Percent of the canvas's own width, not pixels — the StreamView editor and
// the real OBS page render at different pixel dimensions, so a fixed pixel
// width would look like a different proportion of the frame on each. Keep in
// sync with api/src/handlers/settings.ts.
export const MIN_VIDEO_WIDTH = 15;
export const MAX_VIDEO_WIDTH = 70;

// Pixels, not percent — the now-playing box is sized to its text, not the
// canvas (see the `nowPlayingWidth` prop below). Keep in sync with
// api/src/handlers/settings.ts.
export const MIN_NOW_PLAYING_WIDTH = 120;
export const MAX_NOW_PLAYING_WIDTH = 1200;

type OverlayElement = "nowPlaying" | "video";

export interface OverlayLayout {
  nowPlaying: OverlayCorner;
  video: OverlayCorner;
}

interface OverlayCanvasProps {
  layout: OverlayLayout;
  font: string;
  /** Pixels — the now-playing title text only, not its small uppercase label. */
  fontSize: number;
  /** Pixels — caps how wide the now-playing box can grow to fit a long title. */
  nowPlayingWidth: number;
  nowPlaying: { title?: string; youtubeUrl?: string } | null;
  mode: "render" | "edit";
  /** Whether the video embed plays with sound — see the mute toggle in StreamView.tsx. */
  muted: boolean;
  /** Whether the video embed autoplays — only takes effect in "render" mode, see below. */
  autoplay: boolean;
  /** 0-100. */
  videoOpacity: number;
  /** 0-100, percent of the canvas's width (not pixels — see MIN/MAX_VIDEO_WIDTH above). */
  videoWidth: number;
  onLayoutChange?: (layout: OverlayLayout) => void;
}

function cornerStyle(corner: OverlayCorner): CSSProperties {
  const [v, h] = corner.split("-") as ["top" | "bottom", "left" | "right"];
  return { position: "absolute", [v]: "var(--overlay-margin)", [h]: "var(--overlay-margin)" };
}

/** Which quadrant of `rect` a point falls in. */
function cornerAtPoint(rect: DOMRect, clientX: number, clientY: number): OverlayCorner {
  const v = clientY - rect.top < rect.height / 2 ? "top" : "bottom";
  const h = clientX - rect.left < rect.width / 2 ? "left" : "right";
  return `${v}-${h}` as OverlayCorner;
}

/** Imperative handle so a WS-driven play/pause command (see Overlay.tsx) can
 * reach the embed without the video's own iframe leaking out as a prop. */
export interface OverlayCanvasHandle {
  sendPlaybackCommand: (action: "play" | "pause") => void;
}

export const OverlayCanvas = forwardRef<OverlayCanvasHandle, OverlayCanvasProps>(
  function OverlayCanvas(
    {
      layout,
      font,
      fontSize,
      nowPlayingWidth,
      nowPlaying,
      mode,
      muted,
      autoplay,
      videoOpacity,
      videoWidth,
      onLayoutChange,
    },
    ref,
  ) {
  const canvasRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const playerRef = useRef<YTPlayer | null>(null);

  useImperativeHandle(ref, () => ({
    sendPlaybackCommand(action) {
      const player = playerRef.current;
      if (!player) return; // not ready yet — no queue, matches "fire and forget" design
      if (action === "play") player.playVideo();
      else player.pauseVideo();
    },
  }));

  // Attaches a YT.Player to the iframe once it mounts — paired with `key={id}`
  // on the <iframe> below so a new video gets a fresh element (and thus a
  // fresh player) rather than trying to rebind an existing one whose `src`
  // React already changed out from under it. Must be a stable callback
  // identity (useCallback, not an inline function): a React ref callback
  // re-fires detach(null)/attach on every render where its identity changes,
  // which would null out playerRef and rebuild the YT.Player on top of the
  // same still-live iframe on every unrelated re-render (e.g. every
  // WS-driven queue refresh) — leaving sendPlaybackCommand's `if (!player)`
  // guard dropping commands that land in that window.
  const attachPlayer = useCallback((el: HTMLIFrameElement | null) => {
    iframeRef.current = el;
    playerRef.current = null;
    if (!el) return;
    void loadYouTubeIframeApi().then((YT) => {
      if (iframeRef.current !== el) return; // unmounted/swapped before the API loaded
      playerRef.current = new YT.Player(el);
    });
  }, []);
  // Control flow (does a move/up belong to an active drag?) reads this ref,
  // not the state below — a fast pointermove/pointerup can fire before React
  // has flushed the state update from pointerdown, and a stale closure read
  // of state there would silently drop the event.
  const draggingRef = useRef<OverlayElement | null>(null);
  const [dragging, setDragging] = useState<OverlayElement | null>(null); // for rendering only
  const [hoverCorner, setHoverCorner] = useState<OverlayCorner | null>(null);
  // null until the first measurement lands — style falls back to the
  // .overlay-canvas CSS class's plain `inset: 0` (see the JSX below), which
  // is pixel-identical to what fitCanvas() computes once scale is 1 anyway.
  const [canvasFit, setCanvasFit] = useState<ReturnType<typeof fitCanvas> | null>(null);

  // Edit mode: track the actual size of the StreamView preview box
  // (`.streamview-canvas`, this element's parent — always 16:9). Render mode:
  // track the real page's own viewport, since this is a public,
  // unauthenticated page with no sized wrapper of its own — someone loading
  // /overlay/:token straight on a phone (checking it looks right, or an
  // actual mobile-shaped broadcast source) hits the same "fixed px looks
  // huge in a small frame" problem the editor preview did, just with a
  // viewport that isn't locked to 16:9.
  useEffect(() => {
    if (mode === "edit") {
      const el = canvasRef.current?.parentElement;
      if (!el) return;
      const ro = new ResizeObserver(([entry]) => {
        const { width, height } = entry?.contentRect ?? {};
        if (width && height) setCanvasFit(fitCanvas(width, height));
      });
      ro.observe(el);
      return () => ro.disconnect();
    }

    function updateFromViewport() {
      const { innerWidth: width, innerHeight: height } = window;
      if (width && height) setCanvasFit(fitCanvas(width, height));
    }
    updateFromViewport();
    window.addEventListener("resize", updateFromViewport);
    return () => window.removeEventListener("resize", updateFromViewport);
  }, [mode]);

  useEffect(() => {
    ensureOverlayFontLoaded(font);
  }, [font]);

  // oEmbed is CORS-enabled and key-free, so this resolves fine from the public
  // unauthenticated overlay page too — no server-side title work needed.
  const resolvedTitle = useVideoTitle(nowPlaying?.youtubeUrl ?? "");
  const id = nowPlaying?.youtubeUrl ? videoId(nowPlaying.youtubeUrl) : null;
  const title = nowPlaying?.youtubeUrl
    ? resolvedTitle ?? displayTitle(nowPlaying.youtubeUrl)
    : nowPlaying?.title ?? null;

  const editable = mode === "edit" && !!onLayoutChange;
  // Edit mode always shows both boxes — even with an empty queue there must be
  // something to drag, so the streamer can lay the overlay out ahead of time.
  const showNowPlaying = mode === "edit" || !!title;
  const showVideo = mode === "edit" || !!id;

  function moveTo(el: OverlayElement, corner: OverlayCorner) {
    onLayoutChange?.({ ...layout, [el]: corner });
  }

  function cornerAt(clientX: number, clientY: number): OverlayCorner | null {
    const rect = canvasRef.current?.getBoundingClientRect();
    return rect ? cornerAtPoint(rect, clientX, clientY) : null;
  }

  function startDrag(e: PointerEvent<HTMLDivElement>, el: OverlayElement) {
    if (!editable) return;
    e.preventDefault();
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* capture is a nice-to-have (keeps tracking if the pointer leaves the
       * element) — the corner is computed from event coordinates regardless. */
    }
    draggingRef.current = el;
    setDragging(el);
    setHoverCorner(cornerAt(e.clientX, e.clientY));
  }

  function onDragMove(e: PointerEvent<HTMLDivElement>) {
    if (!draggingRef.current) return;
    setHoverCorner(cornerAt(e.clientX, e.clientY));
  }

  function endDrag(e: PointerEvent<HTMLDivElement>) {
    const el = draggingRef.current;
    if (!el) return;
    const corner = cornerAt(e.clientX, e.clientY);
    if (corner) moveTo(el, corner);
    draggingRef.current = null;
    setDragging(null);
    setHoverCorner(null);
  }

  return (
    <div
      className="overlay-canvas"
      ref={canvasRef}
      style={
        canvasFit
          ? {
              top: 0,
              left: 0,
              right: "auto",
              bottom: "auto",
              width: canvasFit.width,
              height: canvasFit.height,
              transform: `scale(${canvasFit.scale})`,
              transformOrigin: "top left",
            }
          : undefined
      }
    >
      {dragging &&
        CORNERS.map((corner) => (
          <div
            key={corner}
            className={`overlay-dropzone ${corner} ${hoverCorner === corner ? "active" : ""}`}
          />
        ))}

      {showNowPlaying && (
        <div
          className={`overlay-now-playing ${title ? "" : "placeholder"} ${editable ? "editable" : ""} ${dragging === "nowPlaying" ? "dragging-source" : ""}`}
          style={{
            ...cornerStyle(layout.nowPlaying),
            fontFamily: overlayFontFamily(font),
            maxWidth: nowPlayingWidth,
          }}
          onPointerDown={(e) => startDrag(e, "nowPlaying")}
          onPointerMove={onDragMove}
          onPointerUp={endDrag}
        >
          <span className="overlay-now-playing-label">▶ Now playing</span>
          {/* 36px = the box's own left+right padding — keeps the title's cap
              in step with the box's so it never becomes the bottleneck. */}
          <span
            className="overlay-now-playing-title"
            style={{ fontSize, maxWidth: nowPlayingWidth - 36 }}
          >
            {title ?? "Song title"}
          </span>
          {editable && (
            <CornerButtons corner={layout.nowPlaying} onPick={(c) => moveTo("nowPlaying", c)} />
          )}
        </div>
      )}

      {showVideo && (
        <div
          className="overlay-video"
          style={{
            ...cornerStyle(layout.video),
            // Percent of the canvas, not px — see MIN/MAX_VIDEO_WIDTH above.
            width: `${videoWidth}%`,
            // Inline opacity beats any CSS class, so the "being moved" dim
            // (previously a .dragging-source class rule) has to fold in here.
            opacity: (videoOpacity / 100) * (dragging === "video" ? 0.5 : 1),
          }}
        >
          <div
            className={`overlay-video-dragbar ${editable ? "editable" : ""}`}
            onPointerDown={(e) => startDrag(e, "video")}
            onPointerMove={onDragMove}
            onPointerUp={endDrag}
          />
          {id ? (
            <iframe
              key={id}
              ref={attachPlayer}
              className="overlay-video-frame"
              // Autoplay only on the real broadcast page (and only if enabled) —
              // the editor would otherwise start playing (and, once unmuted,
              // making noise) every time a streamer opens StreamView to adjust
              // the layout. `enablejsapi=1` is required for the YT.Player
              // control layer attached in attachPlayer (see sendPlaybackCommand).
              src={`https://www.youtube.com/embed/${id}?playsinline=1&controls=0&modestbranding=1&rel=0&enablejsapi=1${mode === "render" && autoplay ? "&autoplay=1" : ""}${muted ? "&mute=1" : ""}`}
              title="Now playing video"
              allow="autoplay; encrypted-media"
              frameBorder={0}
            />
          ) : (
            <div className="overlay-video-placeholder">▶ Video</div>
          )}
          {editable && <CornerButtons corner={layout.video} onPick={(c) => moveTo("video", c)} />}
        </div>
      )}
    </div>
  );
  },
);

/** Keyboard/screen-reader-accessible fallback for corner placement — pointer
 * dragging has no built-in a11y story, so each box also gets 4 plain buttons. */
function CornerButtons({
  corner,
  onPick,
}: {
  corner: OverlayCorner;
  onPick: (c: OverlayCorner) => void;
}) {
  return (
    <div
      className="overlay-corner-buttons"
      role="group"
      aria-label="Move to corner"
      // Stop the click from also bubbling into the box's onPointerDown above,
      // which would start an unwanted drag from a button press.
      onPointerDown={(e) => e.stopPropagation()}
    >
      {CORNERS.map((c) => (
        <button
          key={c}
          type="button"
          className={`overlay-corner-btn ${c} ${c === corner ? "active" : ""}`}
          onClick={() => onPick(c)}
          aria-label={`Move to ${c.replace("-", " ")}`}
          aria-pressed={c === corner}
        />
      ))}
    </div>
  );
}
